/* ─────────────────────────────────────────────────────────
 *  MIDI DAW – Library API client
 *
 *  Thin typed wrapper over the BFF /bff/midi-daw routes.
 *  All digestion happens server-side; the FE consumes
 *  FE-ready shapes only.
 * ────────────────────────────────────────────────────────── */
import { fetchData } from '@/utils/fetch'
import type { MidiProjectSnapshot } from '../services/project-snapshot'

const BASE = '/bff/midi-daw'

// ── FE-ready shapes (mirror the BFF digesters) ─────────────

export interface MidiProjectSummary {
  project_key: string
  name: string
  description: string
  status: string
  version_count: number
  latest_version: number
  latest_version_key: string
  bpm: number | null
  time_sig: string
  track_count: number
  note_count: number
  duration_seconds: number
  created_at: string | null
  updated_at: string | null
}

export interface MidiVersionSummary {
  version_key: string
  project_key: string
  version: number
  label: string
  bpm: number | null
  time_sig: string
  track_count: number
  note_count: number
  duration_seconds: number
  created_at: string | null
}

export interface MidiProjectDetail extends MidiProjectSummary {
  versions: MidiVersionSummary[]
}

export interface VersionSnapshotResponse extends MidiVersionSummary {
  snapshot: MidiProjectSnapshot
}

// ── Calls ──────────────────────────────────────────────────

export async function listMidiProjects(): Promise<MidiProjectSummary[]> {
  const raw = await fetchData(`${BASE}/projects`)
  return Array.isArray(raw?.items) ? (raw.items as MidiProjectSummary[]) : []
}

export async function getMidiProject(projectKey: string): Promise<MidiProjectDetail | null> {
  return (await fetchData(`${BASE}/projects/${encodeURIComponent(projectKey)}`)) as MidiProjectDetail
}

export async function createMidiProject(
  name: string,
  snapshot: MidiProjectSnapshot,
  description = '',
): Promise<{ project_key: string; version: number; version_key: string }> {
  return await fetchData(`${BASE}/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description, snapshot }),
  })
}

export async function saveMidiVersion(
  projectKey: string,
  snapshot: MidiProjectSnapshot,
  label = '',
): Promise<{ version: number; version_key: string }> {
  return await fetchData(`${BASE}/projects/${encodeURIComponent(projectKey)}/versions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ snapshot, label }),
  })
}

export async function loadMidiVersionSnapshot(versionKey: string): Promise<VersionSnapshotResponse> {
  return (await fetchData(`${BASE}/versions/${encodeURIComponent(versionKey)}/snapshot`)) as VersionSnapshotResponse
}
